/**
 * Convert DD-MM-YYYY to YYYY-MM-DD for storage
 * @param ddMmYyyy - Date string in DD-MM-YYYY format
 * @returns ISO date string (YYYY-MM-DD)
 */
export const convertToIsoFormat = (ddMmYyyy: string): string => {
  const [day, month, year] = ddMmYyyy.split('-');
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
};

/**
 * Convert YYYY-MM-DD to DD-MM-YYYY for display
 * @param yyyyMmDd - ISO date string (YYYY-MM-DD)
 * @returns DD-MM-YYYY format string
 */
export const convertToDisplayFormat = (yyyyMmDd: string): string => {
  const [year, month, day] = yyyyMmDd.split('-');
  return `${day}-${month}-${year}`;
};

/**
 * Calculate age from birthdate
 * @param birthdate - ISO date string (YYYY-MM-DD) or DD-MM-YYYY
 * @returns age in years
 */
export const calculateAge = (birthdate: string): number => {
  // Handle both formats
  let isoDate: string;
  if (birthdate.includes('-') && birthdate.split('-')[0].length === 4) {
    // Already in YYYY-MM-DD format
    isoDate = birthdate;
  } else {
    // Assume DD-MM-YYYY format and convert
    isoDate = convertToIsoFormat(birthdate);
  }
  
  const today = new Date();
  const birth = new Date(isoDate);
  
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  
  // If birthday hasn't occurred this year yet, subtract 1
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  
  return age;
};

/**
 * Format birthdate for display
 * @param birthdate - ISO date string (YYYY-MM-DD)
 * @returns formatted date string
 */
export const formatBirthdate = (birthdate: string): string => {
  const date = new Date(birthdate);
  return date.toLocaleDateString('en-AU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Validate DD-MM-YYYY format
 * @param dateString - Date string to validate
 * @returns true if valid DD-MM-YYYY format
 */
export const validateDateFormat = (dateString: string): boolean => {
  const regex = /^\d{2}-\d{2}-\d{4}$/;
  if (!regex.test(dateString)) return false;
  
  const [day, month, year] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  
  return date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year;
};

/**
 * Check if today is the student's birthday
 * @param birthdate - ISO date string (YYYY-MM-DD) or DD-MM-YYYY
 * @returns true if today is the student's birthday
 */
export const isTodayBirthday = (birthdate: string): boolean => {
  if (!birthdate) return false;
  
  // Handle both formats
  let isoDate: string;
  if (birthdate.includes('-') && birthdate.split('-')[0].length === 4) {
    // Already in YYYY-MM-DD format
    isoDate = birthdate;
  } else {
    // Assume DD-MM-YYYY format and convert
    isoDate = convertToIsoFormat(birthdate);
  }
  
  const today = new Date();
  const birth = new Date(isoDate);
  
  // Check if month and day match (ignore year)
  return today.getMonth() === birth.getMonth() && today.getDate() === birth.getDate();
};
